import React, { useEffect, useState } from 'react';
import { Star, Quote, ExternalLink } from 'lucide-react';

const GoogleReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/google/reviews`);
      const data = await response.json();
      
      setRating(data.rating || 0);
      setTotalReviews(data.user_ratings_total || 0);
      setReviews(data.reviews || []);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-5 w-5 ${
          i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Bewertungen werden geladen...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Overall Rating */}
      <div className="bg-gradient-to-br from-brand-green/10 to-brand-blue/10 rounded-2xl p-8 mb-12 text-center">
        <div className="inline-flex items-center justify-center space-x-2 mb-4">
          {renderStars(Math.round(rating))}
        </div>
        <div className="text-5xl font-bold text-gray-900 mb-2">{rating.toFixed(1)}</div>
        <p className="text-gray-600">
          Basierend auf <strong>{totalReviews} echten Google Bewertungen</strong>
        </p>
        <a
          href="https://search.google.com/local/writereview?placeid=ChIJc45hE5OHsUcRJD80m0y_Od0"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center mt-6 px-6 py-3 bg-white border-2 border-brand-green text-brand-green font-semibold rounded-xl hover:bg-brand-green hover:text-white transition-colors"
        >
          Jetzt bewerten
          <ExternalLink className="ml-2 h-4 w-4" />
        </a>
      </div>

      {/* Reviews Grid */}
      {reviews.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reviews.map((review, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  {review.profile_photo_url && (
                    <img
                      src={review.profile_photo_url}
                      alt={review.author_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  )}
                  {!review.profile_photo_url && (
                    <div className="w-12 h-12 rounded-full bg-brand-blue/10 flex items-center justify-center">
                      <span className="text-brand-blue font-bold text-lg">
                        {review.author_name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">{review.author_name}</p>
                    <p className="text-xs text-gray-500">{review.relative_time_description}</p>
                  </div>
                </div>
                <Quote className="h-6 w-6 text-brand-green/30" />
              </div>

              {/* Rating */}
              <div className="flex space-x-1 mb-3">
                {renderStars(review.rating)}
              </div>

              {/* Review Text */}
              <p className="text-gray-700 text-sm leading-relaxed">
                {review.text}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Google Logo Attribution */}
      <div className="text-center mt-8">
        <p className="text-xs text-gray-500">
          Bewertungen bereitgestellt von Google
        </p>
      </div>
    </div>
  );
};

export default GoogleReviews;
